import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { combineLatest, map } from "rxjs";
import {
  CollectionOfComparison,
  CollectionOfCompetitor,
  CollectionOfTechData,
  DefaultComparison,
  DefaultCompetitor,
} from "./generated";
import { ConfigService } from "../config/config.service";
import { CompetitorsGroupedByBrand } from "../resolvers/competitors.resolver";

@Injectable({
  providedIn: "root",
})
export class PiaCompetitorsService {
  configService = inject(ConfigService);
  http = inject(HttpClient);

  getCompetitorsByContext(
    {
      countryID,
      languageID,
      year,
    }: { countryID: string; languageID: string; year: number },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Year(ID=${encodeURIComponent(year.toString())},Country='${encodeURIComponent(countryID)}',Language='${encodeURIComponent(languageID)}')/Competitors`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: CollectionOfCompetitor }>(url, {
      headers,
      context,
    });
  }

  getComparisonByContext(
    {
      countryID,
      languageID,
      year,
    }: { countryID: string; languageID: string; year: number },
    context?: HttpContext,
  ) {
    const url = `${this.configService.piaAPIOrigin}/Comparison?$filter=Year eq ${encodeURIComponent(year.toString())} and Country eq '${encodeURIComponent(countryID)}' and Language eq '${encodeURIComponent(languageID)}'`;
    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: CollectionOfComparison }>(url, {
      headers,
      context,
    });
  }

  getComparisonByContextAndProductId(
    {
      countryID,
      languageID,
      year,
      productID,
    }: {
      countryID: string;
      languageID: string;
      year: number;
      productID: string;
    },
    context?: HttpContext,
  ) {
    return this.getComparisonByContext(
      { countryID, languageID, year },
      context,
    ).pipe(
      map((comparisons) => {
        return comparisons?.d?.results?.filter((comparison) => {
          return comparison.ProductId === productID;
        });
      }),
    );
  }

  getComparableCompetitorsGroupedByBrand(
    {
      countryID,
      languageID,
      year,
      productID,
    }: {
      countryID: string;
      languageID: string;
      year: number;
      productID: string;
    },
    context?: HttpContext,
  ) {
    const contextComparisons$ = this.getComparisonByContextAndProductId(
      {
        countryID,
        languageID,
        year,
        productID,
      },
      context,
    );
    const competitors$ = this.getCompetitorsByContext(
      { countryID, languageID, year },
      context,
    );

    const benchmarkIconIds = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      1: "659463",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      2: "648420",
      // eslint-disable-next-line @typescript-eslint/naming-convention
      3: "659462",
    };

    return combineLatest([contextComparisons$, competitors$]).pipe(
      map(
        ([comparisons, competitorsCollection]: [
          DefaultComparison[] | undefined,
          { d?: CollectionOfCompetitor },
        ]) => {
          const includedCompetitorIds = comparisons?.map(
            (comparison: DefaultComparison) => comparison.CompetitorId,
          );
          const competitors = competitorsCollection?.d?.results;
          const matchingCompetitors = competitors?.filter((competitor) =>
            includedCompetitorIds?.includes(competitor.ID),
          );
          const matchingCompetitorsGroupedByBrand = matchingCompetitors?.reduce(
            (current, competitor) => {
              let competitorParent: DefaultCompetitor | undefined = competitor;
              while (
                competitorParent?.ParentId &&
                !competitorParent?.ID?.startsWith("cpr_bra_")
              ) {
                competitorParent = competitors?.find(
                  (competitorToCheck) =>
                    competitorToCheck.ID === competitorParent?.ParentId,
                );
              }

              if (
                !competitorParent?.ID ||
                !competitorParent?.ID?.startsWith("cpr_bra_")
              ) {
                return current;
              }

              const benchmark = comparisons?.find(
                (comparison) => comparison.CompetitorId === competitor.ID,
              )?.Benchmark;
              competitor = {
                ...competitor,
                ...(benchmark &&
                  !isNaN(benchmark) &&
                  benchmarkIconIds[
                    benchmark as keyof typeof benchmarkIconIds
                  ] && {
                    benchmarkIconId:
                      benchmarkIconIds[
                        benchmark as keyof typeof benchmarkIconIds
                      ],
                  }),
              };

              if (!current) {
                current = {
                  [competitorParent.ID as string]: {
                    brand: competitorParent.Name as string,
                    items: [competitor],
                  },
                };
              } else if (current[competitorParent.ID]) {
                current[competitorParent.ID as string].items = [
                  ...current[competitorParent.ID as string].items,
                  competitor,
                ];
              } else if (!current[competitorParent.ID]) {
                current = {
                  ...current,
                  [competitorParent.ID as string]: {
                    brand: competitorParent.Name as string,
                    items: [competitor],
                  },
                };
              }

              return current;
            },
            null as CompetitorsGroupedByBrand,
          );

          return matchingCompetitorsGroupedByBrand;
        },
      ),
    );
  }

  getCompetitorTechDataByContextAndCompetitorProductId({
    countryID,
    languageID,
    year,
    competitorProductId,
  }: {
    countryID: string;
    languageID: string;
    year: number;
    competitorProductId: string;
  }) {
    const url = `${this.configService.piaAPIOrigin}/Competitor(ID='${encodeURIComponent(competitorProductId)}',Year=${encodeURIComponent(year.toString())},Language='${encodeURIComponent(languageID)}',Country='${encodeURIComponent(countryID)}')/TechData?$expand=Category`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });
    return this.http.get<{ d?: CollectionOfTechData }>(url, { headers });
  }

  getCompetitorDetailsByContextAndCompetitorProductId({
    countryID,
    languageID,
    year,
    competitorProductId,
  }: {
    countryID: string;
    languageID: string;
    year: number;
    competitorProductId: string;
  }) {
    const url = `${this.configService.piaAPIOrigin}/Competitor(ID='${encodeURIComponent(competitorProductId)}',Year=${encodeURIComponent(year.toString())},Language='${encodeURIComponent(languageID)}',Country='${encodeURIComponent(countryID)}')?$expand=Arguments/Argument,Exclude,TechData/Category,Comparisons/Product,Documents/Document/Category`;
    const headers = new HttpHeaders({
      accept: "application/json",
    });
    return this.http.get<{ d?: DefaultCompetitor }>(url, { headers });
  }
}
